import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Bell, Package, Truck, Clock, MapPin, User, Phone, Calendar, CheckCircle, AlertCircle, Camera, FileSignature, Smartphone, Activity, Car, Eye, Building2 } from 'lucide-react';
import { useAppSelector } from '@/redux/hooks';
import axiosInstance from '../../api/axiosInstance';
import { 
  normalizeDeliveryStats, 
  isOrderInTransit, 
  findMongoObjectId,
} from '../../utils/dataUtils';
import { getStatusColor, getStatusIcon, getStatusLabel } from '../../utils/statusUtils';
import { orderService, getDeliveryOrders } from '../../api/orderService';
import { getPickupOrders, requestPickup as requestPickupAPI, type PickupOrder } from '../../api/pickupService';
import { toast } from 'sonner';
import GlobalLoader from "../../components/ui/GlobalLoader";

interface Order {
  _id: string;
  id?: string;
  prescriptionNumber?: string;
  orderNumber?: string;
  patientName?: string;
  customerName?: string;
  patientEmail?: string;
  customerEmail?: string;
  patientPhone?: string;
  customerPhone?: string;
  patientAddress?: string;
  customerAddress?: string;
  deliveryDate?: string;
  deliveryType?: string;
  status: 'arrived-at-facality' | 'picked-up' | 'pending' | 'pickup-requested' | 'in-transit' | 'delivered' | 'failed' | 'assigned' | 'completed';
  medicines?: Array<{
    medicineName: string;
    dosage: string;
    quantity: number;
  }>;
  medications?: string[];
  location?: {
    address?: string;
    longitude?: number;
    latitude?: number;
  }
}

export function PickupPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [pickupOrders, setPickupOrders] = useState<PickupOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [deliveryStats, setDeliveryStats] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [selectAllSameDay, setSelectAllSameDay] = useState<boolean>(false);
  const [selectAllNextDay, setSelectAllNextDay] = useState<boolean>(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inTransit: 0,
    delivered: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pickupRequested, setPickupRequested] = useState(0);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedPickupOrder, setSelectedPickupOrder] = useState<PickupOrder | null>(null);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (user && !user.isVerified) {
      toast.error("Account is not verified. Please wait for admin to verify.", {
        duration: 5000,
        position: "top-center"
      });
    }
  }, [user]);

  const fetchDeliveryOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { orders: deliveryOrders, stats } = await getDeliveryOrders();
      
      const legacyOrders = (deliveryOrders as any[]).map(order => ({
        _id: order._id,
        id: order.id || order._id,
        prescriptionNumber: order.orderNumber,
        orderNumber: order.orderNumber,
        patientName: order.customerName,
        customerName: order.customerName,
        patientEmail: order.customerName,
        customerEmail: order.customerName,
        patientPhone: order.customerPhone,
        customerPhone: order.customerPhone,
        patientAddress: order.customerAddress,
        customerAddress: order.customerAddress,
        deliveryDate: order.deliveryDate,
        deliveryType: order.priority,
        status: order.pickupStatus,
        medicines: order.medicines,
        medications: order.medicines?.map(med => med.name),
        location: order.location
      }));
      
      setOrders(legacyOrders);
      setStats({
        total: stats.totalOrders,
        pending: stats.pendingOrders,
        inTransit: stats.inTransitOrders,
        delivered: stats.deliveredOrders
      });
      
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryStats = async () => {
    try {
      const response = await axiosInstance.get('/delivery/stats');
      if (response.data.data) {
        const normalizedStats = normalizeDeliveryStats(response.data.data);
        setDeliveryStats(normalizedStats);
      }
    } catch (err: any) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchDeliveryOrders();
    fetchDeliveryStats();
    fetchPickupOrdersData();
  }, []);

  useEffect(() => {
    fetchPickupOrdersData();
  }, [currentPage]);

  // Status functions are now imported from utils/statusUtils.tsx

  // Handle view order
  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const handleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllSameDay = (checked: boolean) => {
    if (checked) {
      // Uncheck next day selection
      setSelectAllNextDay(false);
      
      // Clear all current selections and select only same-day orders
      const sameDayOrders = filteredOrders
        .filter(order => order.deliveryType === 'same-day')
        .map(order => order._id);
      
      setSelectedOrders(sameDayOrders);
      setSelectAllSameDay(true);
    } else {
      // Just uncheck same-day selection
      setSelectedOrders(prev => prev.filter(id => {
        const order = filteredOrders.find(o => o._id === id);
        return order?.deliveryType !== 'same-day';
      }));
      setSelectAllSameDay(false);
    }
  };

  const handleSelectAllNextDay = (checked: boolean) => {
    if (checked) {
      // Uncheck same day selection
      setSelectAllSameDay(false);
      
      // Clear all current selections and select only next-day orders
      const nextDayOrders = filteredOrders
        .filter(order => order.deliveryType === 'next-day')
        .map(order => order._id);
      
      setSelectedOrders(nextDayOrders);
      setSelectAllNextDay(true);
    } else {
      // Just uncheck next-day selection
      setSelectedOrders(prev => prev.filter(id => {
        const order = filteredOrders.find(o => o._id === id);
        return order?.deliveryType !== 'next-day';
      }));
      setSelectAllNextDay(false);
    }
  };

  const requestPickup = async () => {
    if (selectedOrders.length === 0) return;
    
    setLoading(true);
    try {
      // Send all order IDs to create a pickup order
      const response = await requestPickupAPI(selectedOrders);
      
      if (response.success) {
        // toast.success(`Pickup requested successfully for ${selectedOrders.length} order(s)`);
        setSelectedOrders([]);
        await fetchPickupOrdersData();
        await fetchDeliveryOrders();
      } else {
        toast.error('Failed to request pickup');
      }
    } catch (err: any) {
      console.error('Error requesting pickup:', err);
      toast.error(err.message || 'Failed to request pickup');
    } finally {
      setLoading(false);
    }
  };

  // Fetch pickup orders
  const fetchPickupOrdersData = async () => {
    try {
      setLoading(true);
      const response = await getPickupOrders({
        pharmacyId: user?.id,
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (response.success) {
        setPickupOrders(response.data);
        console.log('Pickup orders fetched:', response.data);
      }
    } catch (error: any) {
      console.error('Error fetching pickup orders:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const assignToDriver = async (orderId: string, driverId: string) => {
    try {
      const order = orders.find(o => (o._id || o.id) === orderId);
      const mongoId = findMongoObjectId(order!);
      
      if (!mongoId) {
        throw new Error(`No valid MongoDB ObjectId found for order ${orderId}`);
      }
      
      const response = await axiosInstance.post(`/delivery/orders/${mongoId}/assign`, {
        driverId
      });
      
      if (response.data.success) {
        setOrders(prev => prev.map(order => 
          (order._id || order.id) === orderId 
            ? { ...order, driverId }
            : order
        ));
        // toast.success('Driver assigned successfully');
      }
    } catch (err: any) {
      console.error('Error assigning driver:', err);
      toast.error('Failed to assign driver');
    }
  };

  // const getPickupRequested = () => {
  //   return orders.filter(order => order.status === 'pickup-requested').length;
  // };
  useEffect(() => {
    setPickupRequested(orders.filter(order => order.status === 'pickup-requested').length);
  }, [orders]);

  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'same-day':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'next-day':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredOrders = orders.filter(order => {
    // Exclude pickup-requested orders from Order Management tab (they appear in Pickup Requests tab)
    if (order.status !== "pending") {
      return false;
    }

    const searchMatch = searchTerm === '' || 
      (order.prescriptionNumber || order.orderNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.patientName || order.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.patientPhone || order.customerPhone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.patientEmail || order.customerEmail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.patientAddress || order.customerAddress || '').toLowerCase().includes(searchTerm.toLowerCase());

    const priorityMatch = priorityFilter === 'all' || order.deliveryType === priorityFilter;

    return searchMatch && priorityMatch;
  });

  // Update select all checkboxes based on current selections (mutually exclusive)
  useEffect(() => {
    const sameDayOrders = filteredOrders.filter(order => order.deliveryType === 'same-day');
    const nextDayOrders = filteredOrders.filter(order => order.deliveryType === 'next-day');
    
    const selectedSameDayCount = selectedOrders.filter(id => {
      const order = filteredOrders.find(o => o._id === id);
      return order?.deliveryType === 'same-day';
    }).length;
    
    const selectedNextDayCount = selectedOrders.filter(id => {
      const order = filteredOrders.find(o => o._id === id);
      return order?.deliveryType === 'next-day';
    }).length;
    
    // Check if only same-day orders are selected
    const onlySameDaySelected = sameDayOrders.length > 0 && 
      selectedSameDayCount === sameDayOrders.length && 
      selectedNextDayCount === 0;
    
    // Check if only next-day orders are selected
    const onlyNextDaySelected = nextDayOrders.length > 0 && 
      selectedNextDayCount === nextDayOrders.length && 
      selectedSameDayCount === 0;
    
    setSelectAllSameDay(onlySameDaySelected);
    setSelectAllNextDay(onlyNextDaySelected);
  }, [selectedOrders, filteredOrders]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, priorityFilter]);

  const clearFilters = () => {
    setSearchTerm('');
    setPriorityFilter('all');
    setSelectedOrders([]);
    setSelectAllSameDay(false);
    setSelectAllNextDay(false);
  };
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'f':
            e.preventDefault();
            (document.querySelector('input[placeholder*="Search"]') as HTMLInputElement)?.focus();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchDeliveryOrders} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          {/* <h1 className="text-2xl font-semibold text-gray-900">
            Pickup Requests
          </h1> */}
          <p className="text-gray-600">Manage and schedule order pickups</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Orders</p>
                <p className="text-2xl font-bold text-gray-900">{filteredOrders.length}</p>
                <p className="text-xs text-gray-500">of {orders.length} total</p>
              </div>
              <Package className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pickup Requested</p>
                <p className="text-2xl font-bold text-gray-900">{pickupRequested}</p>
                <p className="text-xs text-gray-500">of {orders.length} total</p>
              </div>
              <Car className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        {/* <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{
                  filteredOrders.filter(o => o.status === 'pending').length
                }</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card> */}

        {/* <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pickup Requested</p>
                <p className="text-2xl font-bold text-blue-600">{filteredOrders.filter(o => o.status === 'pickup-requested').length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card> */}

        {/* <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Transit</p>
                <p className="text-2xl font-bold text-purple-600">{filteredOrders.filter(isOrderInTransit).length}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card> */}
        
      </div>

      <Tabs defaultValue="orders" className="space-y-6">
        {/* className="grid w-full max-w-md grid-cols-2"  */}
        <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="orders"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            Request Pickup
            </TabsTrigger>
          <TabsTrigger value="pickup"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            Pickup Requests
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Request Pickup</CardTitle>
                {/* <div className="flex items-center space-x-2">
                  <Button 
                    onClick={requestPickup} 
                    disabled={selectedOrders.length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Request Pickup ({selectedOrders.length})
                  </Button>
                </div> */}
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search 
                      <span className="text-xs text-gray-500 ml-2">(Ctrl+F to focus)</span>
                    </label>
                    <Input 
                      placeholder="Search by order number, patient name, phone, or address..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full h-11"
                    />
                    {searchTerm && (
                      <p className="text-xs text-gray-500 mt-1">
                        Found {filteredOrders.length} result{filteredOrders.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="same-day">Same Day</SelectItem>
                        <SelectItem value="next-day">Next Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(searchTerm || priorityFilter !== 'all') && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {searchTerm && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <span>Search: "{searchTerm}"</span>
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="ml-1 hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                     {priorityFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <span>Priority: {priorityFilter}</span>
                        <button 
                          onClick={() => setPriorityFilter('all')}
                          className="ml-1 hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      Showing {filteredOrders.length} of {orders.length} orders
                    </span>
                    {(searchTerm || priorityFilter !== 'all') && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={clearFilters}
                        className="text-gray-600"
                      >
                        Clear All Filters
                      </Button>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {filteredOrders.length === 0 && orders.length > 0 && 'No orders match your filters'}
                  </div>
                </div>
              </div>

              {/* Select All Priority Options */}
              {/* <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200"> */}
              <div className="mb-6">
                <div className="flex items-center justify-end">
                  {/* <div>
                    <h3 className="text-sm font-medium text-blue-900 mb-1">Quick Select by Priority</h3>
                    <p className="text-xs text-blue-700">Choose one priority type - selections are mutually exclusive</p>
                  </div> */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <input 
                        type="radio"
                        name="priority-selection"
                        id="select-all-same-day"
                        checked={selectAllSameDay}
                        onChange={(e) => handleSelectAllSameDay(e.target.checked)}
                        className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300"
                      />
                      <label htmlFor="select-all-same-day" className="text-sm font-medium text-red-700">
                        Select All Same Day ({filteredOrders.filter(order => order.deliveryType === 'same-day').length})
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input 
                        type="radio"
                        name="priority-selection"
                        id="select-all-next-day"
                        checked={selectAllNextDay}
                        onChange={(e) => handleSelectAllNextDay(e.target.checked)}
                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300"
                      />
                      <label htmlFor="select-all-next-day" className="text-sm font-medium text-green-700">
                        Select All Next Day ({filteredOrders.filter(order => order.deliveryType === 'next-day').length})
                      </label>
                    </div>
                    <Button 
                    onClick={requestPickup} 
                    disabled={selectedOrders.length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Request Pickup ({selectedOrders.length})
                  </Button>
                  </div>
                </div>
                {selectedOrders.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-blue-800">
                        <strong>{selectedOrders.length}</strong> order{selectedOrders.length !== 1 ? 's' : ''} selected for pickup
                      </p>
                      <Button 
                        onClick={() => {
                          setSelectedOrders([]);
                          setSelectAllSameDay(false);
                          setSelectAllNextDay(false);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-300 hover:bg-blue-100"
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {paginatedOrders?.map((order, index) => (
                  <div key={`${order._id}-${index}`} className="border rounded-lg p-5 bg-white hover:shadow-md transition-shadow">
                    {/* Header Section */}
                    <div className="flex items-start justify-between mb-4 pb-4 border-b">
                      <div className="flex items-center space-x-3 flex-1">
                        <input 
                          type="checkbox"
                          checked={selectedOrders.includes(order._id)}
                          onChange={() => handleOrderSelection(order._id)}
                          className="w-5 h-5 text-green-600 rounded focus:ring-green-500 mt-1"
                        />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {order.prescriptionNumber || order.orderNumber}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">ID: {order._id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(order.status)}>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(order.status)}
                            <span className="capitalize">{order.status}</span>
                          </div>
                        </Badge>
                        {order.deliveryType && (
                          <Badge variant="outline" className={getPriorityColor(order.deliveryType)}>
                            {order.deliveryType === 'same-day' ? 'Same Day' : 'Next Day'}
                          </Badge>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleViewOrder(order)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                      {/* Patient Info */}
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">Patient</span>
                        </div>
                        <p className="font-medium text-gray-900">{order.patientName || order.customerName || 'N/A'}</p>
                        {(order.patientPhone || order.customerPhone) && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            <span>{order.patientPhone || order.customerPhone}</span>
                          </div>
                        )}
                      </div>

                      {/* Location Info */}
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-gray-700">Address</span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {order.location?.address || order.customerAddress || 'N/A'}
                        </p>
                        {order.deliveryDate && (
                          <div className="flex items-center gap-1 text-sm text-gray-600 mt-2">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(order.deliveryDate).toLocaleDateString()}</span>
                            {(order as any).deliveryTime && (
                              <>
                                <Clock className="w-3 h-3 ml-2" />
                                <span>{(order as any).deliveryTime}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Medications Info */}
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">Medications</span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {order?.medicines?.map((m) => m.medicineName).join(', ') || 
                           order?.medications?.join(', ') || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredOrders.length)} of {filteredOrders.length} orders
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pickup">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
              <CardTitle>Pickup Requests</CardTitle>
                <Badge variant="outline" className="text-lg px-3 py-1">
                  {pickupOrders.length} Pickup Orders
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Input 
                        placeholder="Search by order number, customer name, or phone..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-11"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="same-day">Same Day</SelectItem>
                        <SelectItem value="next-day">Next Day</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" className="h-11" size="lg" onClick={() => { setSearchTerm(''); setPriorityFilter('all'); }}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>

              {/* Pickup Orders List */}
              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-12">
                    <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
                    <p className="mt-4 text-gray-600">Loading pickup orders...</p>
                  </div>
                ) : pickupOrders.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No pickup requests at the moment</p>
                    <p className="text-sm mt-2">Create a pickup request from the "Request Pickup" tab</p>
                  </div>
                ) : (
                  pickupOrders.map((pickupOrder) => (
                    <Card key={pickupOrder._id} className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
                      <CardHeader className="bg-blue-100/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Truck className="w-6 h-6 text-blue-600" />
                            <div>
                              <h3 className="font-semibold text-lg">
                              <Badge
                                variant="outline"
                                className="text-2xl mr-3"
                              >
                                <Building2 className="w-3 h-3 mr-1" />
                                {pickupOrder.pharmacyName ||
                                  "Unknown Pharmacy"}
                              </Badge>
                                Pickup Order #{pickupOrder.pickupOrderId || pickupOrder._id.substring(0, 8)}
                              </h3>
                              <p className="text-sm text-gray-600 my-3">
                                {pickupOrder.totalOrders} {pickupOrder.totalOrders === 1 ? 'Order' : 'Orders'} • 
                                Requested {new Date(pickupOrder.requestedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              pickupOrder.status === 'pickup-requested' ? 'bg-yellow-100 text-yellow-800' :
                              pickupOrder.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                              pickupOrder.status === 'picked-up' ? 'bg-purple-100 text-purple-800' :
                              pickupOrder.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {pickupOrder.status.replace('-', ' ').toUpperCase()}
                      </Badge>
                    </div>
                        </div>
                        {pickupOrder.driver && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                            <User className="w-4 h-4" />
                            <span>Driver: {pickupOrder.driver.name}</span>
                            <Phone className="w-4 h-4 ml-2" />
                            <span>{pickupOrder.driver.phone}</span>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Nested Orders */}
                        <div className="space-y-3">
                          {pickupOrder.orders && pickupOrder.orders.length > 0 ? (
                            pickupOrder.orders.map((order: any, index: number) => (
                              <div key={order._id || index} className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    {/* Order Header */}
                                    <div className="flex items-center gap-2 mb-3">
                                      <Package className="w-5 h-5 text-purple-600" />
                                      <h4 className="font-semibold text-gray-900">
                                        {order.orderNumber || order.prescriptionNumber || `Order ${index + 1}`}
                                      </h4>
                                      <Badge variant="outline" className="text-xs capitalize">
                                        {order.status}
                                      </Badge>
                                      {order.priority && (
                                        <Badge variant="outline" className={`text-xs ${getPriorityColor(order.priority)}`}>
                                          {order.priority === 'same-day' ? 'Same Day' : 'Next Day'}
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    {/* Order Details Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {/* Customer Info */}
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-1 text-xs text-gray-500 uppercase font-medium">
                                          <User className="w-3 h-3" />
                                          <span>Customer</span>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {order.customerName || order.patientName || 'N/A'}
                                        </p>
                                        {(order.customerPhone || order.patientPhone) && (
                                          <div className="flex items-center gap-1 text-sm text-gray-600">
                                            <Phone className="w-3 h-3" />
                                            <span>{order.customerPhone || order.patientPhone}</span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Location Info */}
                                      {order.location?.address && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-1 text-xs text-gray-500 uppercase font-medium">
                                            <MapPin className="w-3 h-3" />
                                            <span>Location</span>
                                          </div>
                                          <p className="text-sm text-gray-700 line-clamp-2">
                                            {order.location.address}
                                          </p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Medications Preview */}
                                    {(order.medicines || order.medications) && (
                                      <div className="mt-3 pt-3 border-t">
                                        <p className="text-xs text-gray-500 uppercase font-medium mb-2">Medications</p>
                                        <p className="text-sm text-gray-700 line-clamp-1">
                                          {order.medicines?.map((m: any) => m.medicineName || m.name).join(', ') || 
                                           order.medications?.join(', ') || 'N/A'}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* View Button */}
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => {
                                      setSelectedOrder(order);
                                      setViewDialogOpen(true);
                                    }}
                                    className="shrink-0"
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View Details
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              <p className="text-sm">No orders in this pickup</p>
                            </div>
                          )}
              </div>

                        {/* Pickup Order Actions */}
                        {pickupOrder.notes && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <strong>Notes:</strong> {pickupOrder.notes}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Pagination */}
              {pickupOrders.length > 0 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {pickupOrders.length} pickup {pickupOrders.length === 1 ? 'order' : 'orders'}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={pickupOrders.length < itemsPerPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl">Order Details</DialogTitle>
            <DialogDescription>
              Complete information for {selectedOrder?.orderNumber || selectedOrder?.prescriptionNumber || 'this order'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-5">
              {/* Order Header with Status */}
              <div className="flex items-center justify-between pb-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedOrder.orderNumber || selectedOrder.prescriptionNumber || `Order #${selectedOrder._id?.slice(-6)}`}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">ID: {selectedOrder._id}</p>
                </div>
                <Badge className={`${getStatusColor(selectedOrder.status)} text-sm px-3 py-1`}>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(selectedOrder.status)}
                    <span className="capitalize">{selectedOrder.status}</span>
                  </div>
                </Badge>
              </div>

              {/* Customer Information */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Customer Information</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 uppercase mb-1">Name</p>
                    <p className="font-medium text-gray-900">
                      {selectedOrder.customerName || selectedOrder.patientName || 'N/A'}
                    </p>
                  </div>
                  {(selectedOrder.customerPhone || selectedOrder.patientPhone) && (
                    <div>
                      <p className="text-xs text-gray-600 uppercase mb-1">Phone</p>
                      <div className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <p className="font-medium text-gray-900">
                          {selectedOrder.customerPhone || selectedOrder.patientPhone}
                        </p>
                      </div>
                    </div>
                  )}
                  {(selectedOrder.customerEmail || selectedOrder.patientEmail) && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-600 uppercase mb-1">Email</p>
                      <p className="font-medium text-gray-900">
                        {selectedOrder.customerEmail || selectedOrder.patientEmail}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Location */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Delivery Location</h4>
                </div>
                <p className="text-sm text-gray-900 mb-2">
                  {typeof selectedOrder.location === 'object' && selectedOrder.location?.address
                    ? selectedOrder.location.address
                    : selectedOrder.customerAddress || selectedOrder.patientAddress || 'Location not specified'}
                </p>
                {typeof selectedOrder.location === 'object' && selectedOrder.location?.latitude && (
                  <p className="text-xs text-gray-600">
                    📍 Coordinates: {selectedOrder.location.latitude.toFixed(6)}, {selectedOrder.location.longitude.toFixed(6)}
                  </p>
                )}
                {selectedOrder.deliveryDate && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 pt-3 border-t border-green-200">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-600 uppercase">Delivery Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(selectedOrder.deliveryDate).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                    {(selectedOrder as any).deliveryTime && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <div>
                          <p className="text-xs text-gray-600 uppercase">Delivery Time</p>
                          <p className="text-sm font-medium text-gray-900">{(selectedOrder as any).deliveryTime}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Medications List */}
              {(selectedOrder.medicines || selectedOrder.medications) && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">
                      Medications ({selectedOrder.medicines?.length || selectedOrder.medications?.length || 0})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedOrder.medicines?.map((medicine: any, index: number) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded p-3">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {medicine.medicineName || medicine.name || 'N/A'}
                          </p>
                          {medicine.dosage && (
                            <p className="text-xs text-gray-600 mt-1">Dosage: {medicine.dosage}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">Qty: {medicine.quantity || 1}</p>
                        </div>
                      </div>
                    ))}
                    {!selectedOrder.medicines && selectedOrder.medications?.map((med: string, index: number) => (
                      <div key={index} className="bg-white rounded p-3">
                        <p className="font-medium text-gray-900">{med}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

